import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useChat } from '../contexts/ChatContext'
import useWebRTC from '../hooks/useWebRTC'
import LiveCallGraph from './LiveCallGraph'
import { avatarColorIndex, Tip } from '@ofself/shell'
import { uploadTrackRecordings, toggleRaiseHand } from '../services/api'

// Static style objects — prevents React from re-applying styles on every render
const videoVisibleStyle = { display: 'block' }
const videoHiddenStyle = { display: 'none' }

// THE kit hash — one hue per name (same as avatars), app aliases on the kit palette.
function tileColor(name) {
  return `var(--avatar-${avatarColorIndex(name)})`
}

const VideoTile = React.memo(function VideoTile({ stream, muted, mirrored, label, isScreen, videoOn, isMutedIndicator, isSpeaking, handRaised, onPin, isPinned }) {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [trackActive, setTrackActive] = useState(false)
  const cleanupRef = useRef(null)
  const trackDebounceRef = useRef(null)

  // Ref callback: attach srcObject the instant the <video> element mounts
  const videoRefCallback = useCallback((el) => {
    videoRef.current = el
    if (el && stream) {
      el.srcObject = stream
      el.play().catch(() => {})
    }
  }, [stream])

  // Attach stream to video element — use a ref to avoid useEffect churn
  // This runs on every render but only touches the DOM when stream actually changes
  const el = videoRef.current
  if (el && stream !== streamRef.current) {
    // Clean up previous listeners
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null }
    if (trackDebounceRef.current) { clearTimeout(trackDebounceRef.current); trackDebounceRef.current = null }

    streamRef.current = stream
    if (stream) {
      el.srcObject = stream
      el.play().catch(() => {})

      function checkTrack() {
        const vt = stream.getVideoTracks()
        // Only check enabled + readyState; ignore transient .muted which fires
        // during window resizes, tab switches, and renegotiation.
        const active = vt.some(t => t.enabled && t.readyState === 'live')
        // Debounce going inactive (prevents black flash from brief mute during renegotiation/resize)
        if (!active) {
          if (trackDebounceRef.current) return // already waiting
          trackDebounceRef.current = setTimeout(() => {
            trackDebounceRef.current = null
            // Re-check — the track may have come back
            const vtNow = stream.getVideoTracks()
            const stillInactive = !vtNow.some(t => t.enabled && t.readyState === 'live')
            if (stillInactive) setTrackActive(false)
          }, 800) // 800ms grace period — resist resize/tab-switch transients
        } else {
          if (trackDebounceRef.current) { clearTimeout(trackDebounceRef.current); trackDebounceRef.current = null }
          setTrackActive(true)
        }
      }
      checkTrack()

      const tracks = stream.getVideoTracks()
      for (const t of tracks) {
        t.addEventListener('mute', checkTrack)
        t.addEventListener('unmute', checkTrack)
        t.addEventListener('ended', checkTrack)
      }
      stream.addEventListener('addtrack', checkTrack)
      stream.addEventListener('removetrack', checkTrack)
      cleanupRef.current = () => {
        for (const t of tracks) {
          t.removeEventListener('mute', checkTrack)
          t.removeEventListener('unmute', checkTrack)
          t.removeEventListener('ended', checkTrack)
        }
        stream.removeEventListener('addtrack', checkTrack)
        stream.removeEventListener('removetrack', checkTrack)
      }
    } else {
      el.srcObject = null
      setTrackActive(false)
    }
  }

  // Also handle initial mount (videoRef won't be set on first render)
  // This duplicates the render-phase logic but is needed because videoRef is null on first render
  useEffect(() => {
    const el = videoRef.current
    if (!el || !stream) return
    if (streamRef.current === stream) return

    // Clean up previous listeners
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null }
    if (trackDebounceRef.current) { clearTimeout(trackDebounceRef.current); trackDebounceRef.current = null }

    streamRef.current = stream
    el.srcObject = stream
    el.play().catch(() => {})

    function checkTrack() {
      const vt = stream.getVideoTracks()
      const active = vt.some(t => t.enabled && t.readyState === 'live')
      if (!active) {
        if (trackDebounceRef.current) return
        trackDebounceRef.current = setTimeout(() => {
          trackDebounceRef.current = null
          const vtNow = stream.getVideoTracks()
          const stillInactive = !vtNow.some(t => t.enabled && t.readyState === 'live')
          if (stillInactive) setTrackActive(false)
        }, 800)
      } else {
        if (trackDebounceRef.current) { clearTimeout(trackDebounceRef.current); trackDebounceRef.current = null }
        setTrackActive(true)
      }
    }
    checkTrack()

    const tracks = stream.getVideoTracks()
    for (const t of tracks) {
      t.addEventListener('mute', checkTrack)
      t.addEventListener('unmute', checkTrack)
      t.addEventListener('ended', checkTrack)
    }
    stream.addEventListener('addtrack', checkTrack)
    stream.addEventListener('removetrack', checkTrack)
    cleanupRef.current = () => {
      for (const t of tracks) {
        t.removeEventListener('mute', checkTrack)
        t.removeEventListener('unmute', checkTrack)
        t.removeEventListener('ended', checkTrack)
      }
      stream.removeEventListener('addtrack', checkTrack)
      stream.removeEventListener('removetrack', checkTrack)
    }
  }, [stream])

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (cleanupRef.current) cleanupRef.current() }
  }, [])

  // Ensure srcObject is attached after mount/mode-switch, and poll for track changes
  useEffect(() => {
    if (!stream) return
    // Immediately attach srcObject on mount (handles mode switches where useEffect([stream]) might not fire)
    const el = videoRef.current
    if (el && el.srcObject !== stream) {
      el.srcObject = stream
      el.play().catch(() => {})
    }
    // Poll every 500ms as fallback for in-place track additions
    const interval = setInterval(() => {
      const el = videoRef.current
      if (el && el.srcObject !== stream) {
        el.srcObject = stream
        el.play().catch(() => {})
      }
      // Update trackActive for remote tiles
      if (videoOn === undefined) {
        const vt = stream.getVideoTracks()
        const active = vt.some(t => t.enabled && t.readyState === 'live')
        setTrackActive(prev => active !== prev ? active : prev)
      }
    }, 500)
    return () => clearInterval(interval)
  }, [stream, videoOn])

  // For local tile: use explicit videoOn prop. For remote tiles: show video if stream has any video tracks
  const hasStream = !!stream
  const hasVideoTrack = hasStream && stream.getVideoTracks().length > 0
  const showVideo = videoOn !== undefined ? videoOn : (hasVideoTrack || trackActive)
  const initial = (label || '?').slice(0, 2).toUpperCase()

  const tileClasses = [
    'huddle-video-tile',
    isScreen ? 'huddle-video-tile--screen' : '',
    isSpeaking ? 'huddle-video-tile--speaking' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={tileClasses}>
      {/* Always render the video element so it stays attached to the stream */}
      <video
        ref={videoRefCallback}
        autoPlay
        playsInline
        muted={muted}
        className={`huddle-video-elem ${mirrored ? 'huddle-video-local' : ''}`}
        style={showVideo ? videoVisibleStyle : videoHiddenStyle}
      />
      {showVideo ? (
        <span className="huddle-video-name">{label}</span>
      ) : (
        <div className="huddle-video-placeholder">
          <div className="huddle-video-placeholder-avatar" style={{ background: tileColor(label) }}>
            {initial}
          </div>
          <div className="huddle-video-placeholder-name">{label}</div>
        </div>
      )}
      {/* Mute indicator badge */}
      {isMutedIndicator && (
        <Tip label="Muted">
          <div className="huddle-tile-mute-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff4444" strokeWidth="2.5">
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.5-.34 2.18" />
            </svg>
          </div>
        </Tip>
      )}
      {/* Speaking wave indicator */}
      {isSpeaking && (
        <div className="huddle-tile-speaking-wave">
          <div className="huddle-wave-bar" /><div className="huddle-wave-bar" /><div className="huddle-wave-bar" />
        </div>
      )}
      {/* Hand raised badge */}
      {handRaised && (
        <div className="huddle-tile-hand-badge">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 11V6a2 2 0 0 0-4 0v5" />
            <path d="M14 10V4a2 2 0 0 0-4 0v6" />
            <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
            <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
          </svg>
        </div>
      )}
      {/* Pin/unpin button — visible on hover */}
      {onPin && (
        <Tip label={isPinned ? 'Unpin' : 'Pin to enlarge'}>
        <button
          className="huddle-tile-pin-btn"
          onClick={(e) => { e.stopPropagation(); onPin() }}
        >
          {isPinned ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="17" x2="12" y2="22" />
              <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24z" />
            </svg>
          )}
        </button>
        </Tip>
      )}
    </div>
  )
})

// ── Icons ──────────────────────────────────────────
function MicIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
}
function MicOffIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" /><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.5-.34 2.18" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
}
function VideoOnIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
}
function VideoOffIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
}
function ScreenIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
}
function LeaveIcon() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 5.64a9 9 0 1 1-12.73 0" /><line x1="12" y1="2" x2="12" y2="12" /></svg>
}
function MinimizeIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
}
function ExpandIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
}
function HeadphoneIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></svg>
}
function CollapseIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
}
function GraphIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5"  cy="12" r="2.5" />
      <circle cx="19" cy="5"  r="2.5" />
      <circle cx="19" cy="19" r="2.5" />
      <circle cx="12" cy="12" r="1.5" />
      <line x1="7.4"  y1="11.1" x2="10.5" y2="11.7" />
      <line x1="13.5" y1="11.2" x2="16.6" y2="6.8"  />
      <line x1="13.5" y1="12.8" x2="16.6" y2="17.2" />
    </svg>
  )
}

/** Decodes a WebM audio blob and returns its real duration in seconds.
 *  Used so Synapse stores the actual recorded length, not the wall-clock call span. */
async function getAudioDuration(blob) {
  try {
    const arrayBuffer = await blob.arrayBuffer()
    const ctx = new AudioContext()
    const buffer = await ctx.decodeAudioData(arrayBuffer)
    ctx.close()
    return buffer.duration
  } catch {
    return null
  }
}

function playTing() {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 800
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)
    osc.connect(gain).connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.2)
  } catch {}
}

// Warm ascending two-tone chime — someone joined
function playJoinSound() {
  try {
    const ctx = new AudioContext()
    const gain = ctx.createGain()
    gain.connect(ctx.destination)
    // Two ascending notes: D5 then F#5
    ;[587, 740].forEach((freq, i) => {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.value = freq
      const g = ctx.createGain()
      const t = ctx.currentTime + i * 0.12
      g.gain.setValueAtTime(0, t)
      g.gain.linearRampToValueAtTime(0.25, t + 0.03)
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.22)
      osc.connect(g).connect(ctx.destination)
      osc.start(t)
      osc.stop(t + 0.22)
    })
  } catch {}
}

// Quiet descending single tone — someone left
function playLeaveSound() {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(520, ctx.currentTime)
    osc.frequency.linearRampToValueAtTime(380, ctx.currentTime + 0.25)
    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
    osc.connect(gain).connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.25)
  } catch {}
}

export default function HuddleBar() {
  const { joinedHuddle, activeHuddle, activeChannel, currentUser, leaveHuddle, toggleHuddleMute } = useChat()
  // Use joinedHuddle (persists across channel switches) for call state;
  // fall back to activeHuddle for backwards compat
  const huddle = joinedHuddle || activeHuddle
  const [elapsed, setElapsed] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const [huddleMode, setHuddleMode] = useState('fullscreen') // 'fullscreen' | 'pip' | 'bar'
  const timerRef = useRef(null)
  const joinedRef = useRef(false)
  const joinedAtRef = useRef(null)

  // ── Multi-tab leader election ────────────────────────────────────────────────
  // Only ONE tab runs WebRTC at a time. Opening a second Synapse tab must not
  // create a second LiveKit connection with the same identity — LiveKit would
  // kick out the first connection, causing a disconnect/reconnect ping-pong.
  //
  // The "leader" tab holds the LiveKit connection and broadcasts a heartbeat
  // every 1.5 s. Standby tabs watch for the heartbeat:
  //   • If they hear one → don't start, another tab is active.
  //   • If heartbeats stop for 4 s (leader tab closed/navigated) → take over.
  //   • If two tabs start simultaneously (race on page load) → the one that
  //     hears the other's heartbeat first yields immediately.
  const tabIdRef = useRef(`tab_${Math.random().toString(36).slice(2, 8)}`)
  const bcRef = useRef(null)
  const isLeaderRef = useRef(false)
  const leaderHeartbeatTimerRef = useRef(null)
  const lastRemoteHeartbeatRef = useRef(0) // ms timestamp of last heartbeat from another tab
  const isParticipantRef = useRef(false)   // mirror of isParticipant for use inside bc.onmessage

  function _becomeLeader() {
    isLeaderRef.current = true
    clearInterval(leaderHeartbeatTimerRef.current)
    leaderHeartbeatTimerRef.current = setInterval(() => {
      bcRef.current?.postMessage({ type: 'heartbeat', tabId: tabIdRef.current })
    }, 1500)
  }

  function _releaseLeadership(broadcast = true) {
    clearInterval(leaderHeartbeatTimerRef.current)
    leaderHeartbeatTimerRef.current = null
    if (isLeaderRef.current) {
      isLeaderRef.current = false
      if (broadcast) bcRef.current?.postMessage({ type: 'released', tabId: tabIdRef.current })
    }
  }

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const bc = new BroadcastChannel('synapse_webrtc')
    bcRef.current = bc

    bc.onmessage = ({ data }) => {
      if (!data || data.tabId === tabIdRef.current) return

      if (data.type === 'heartbeat') {
        lastRemoteHeartbeatRef.current = Date.now()
        // Race condition: two tabs started simultaneously — yield to the other
        if (isLeaderRef.current && joinedRef.current) {
          _releaseLeadership(false) // don't broadcast; other tab is already the leader
          joinedRef.current = false
          stopRef.current()
        }
      }

      if (data.type === 'released') {
        // The leader gave up — take over if we're still a participant
        lastRemoteHeartbeatRef.current = 0
        if (isParticipantRef.current && !joinedRef.current) {
          joinedRef.current = true
          joinedAtRef.current = Date.now()
          _becomeLeader()
          startRef.current()
        }
      }
    }

    // When this tab becomes visible again, check if the leader is still alive
    function handleVisibility() {
      if (document.visibilityState !== 'visible') return
      if (!isParticipantRef.current || joinedRef.current) return
      const stale = Date.now() - lastRemoteHeartbeatRef.current > 4000
      if (stale) {
        joinedRef.current = true
        joinedAtRef.current = Date.now()
        _becomeLeader()
        startRef.current()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      _releaseLeadership(true)
      bc.close()
      bcRef.current = null
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Allow external components (e.g. CallIsland in the channel header) to
  // request a mode change via a window-level custom event.
  useEffect(() => {
    const handler = (e) => {
      const mode = e?.detail
      if (mode === 'fullscreen' || mode === 'pip' || mode === 'bar') {
        setHuddleMode(mode)
      }
    }
    window.addEventListener('paradigm:huddle-set-mode', handler)
    return () => window.removeEventListener('paradigm:huddle-set-mode', handler)
  }, [])

  // Live knowledge graph panel toggle
  const [showGraph, setShowGraph] = useState(false)

  // PiP drag state
  const [pipPos, setPipPos] = useState({ x: -1, y: -1 }) // -1 = not initialized
  const dragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })
  // Track previous hand_raised states for ting sound
  const prevHandsRef = useRef(new Set())
  const prevParticipantIdsRef = useRef(null) // null = not yet initialized (skip first render)
  // Pin a tile to enlarge it (null = normal grid, string = tileKey to enlarge)
  const [pinnedKey, setPinnedKey] = useState(null)

  const {
    start, stop, toggleMute, isMuted,
    toggleVideo, isVideoOn, videoAvailable,
    toggleScreenShare, isScreenSharing, screenStream,
    localStream, remoteStreams, remoteScreenStreams,
    getTrackRecordings, isRecording,
    activeSpeakers,
  } = useWebRTC({
    huddle: huddle,
    currentUser,
  })

  const isParticipant = huddle?.participants?.some(p => p.user_id === currentUser?.id)
  isParticipantRef.current = isParticipant  // keep bc.onmessage in sync

  // Use refs for start/stop/getTrackRecordings to avoid re-triggering the effect when callbacks change
  const startRef = useRef(start)
  const stopRef = useRef(stop)
  const getTrackRecordingsRef = useRef(getTrackRecordings)
  startRef.current = start
  stopRef.current = stop
  getTrackRecordingsRef.current = getTrackRecordings

  // Track last known huddle/channel IDs — anchored at join time, never re-bound
  // by activeChannel changes. This ensures recordings upload to the correct channel
  // even if the user browses to a different channel mid-call.
  const lastHuddleIdRef = useRef(null)
  const lastChannelIdRef = useRef(null)
  // Channel display info anchored at join time so the call header/bar always
  // shows the huddle's own channel — not whatever channel the user navigated to.
  const joinedChannelNameRef = useRef(null)
  const joinedChannelIsDMRef = useRef(false)

  useEffect(() => {
    if (isParticipant && !joinedRef.current) {
      // Stand by if another tab is already running WebRTC (heard a heartbeat < 4s ago).
      // That tab is the leader; we must not create a second LiveKit connection with the
      // same identity or LiveKit will kick the leader out, causing a blackout loop.
      const timeSinceRemoteHeartbeat = Date.now() - lastRemoteHeartbeatRef.current
      if (timeSinceRemoteHeartbeat < 4000) {
        return
      }

      // Anchor channel/huddle IDs at join time from the huddle object itself.
      // Never re-derive from activeChannel (which changes when user browses channels).
      lastHuddleIdRef.current = huddle?.id
      lastChannelIdRef.current = huddle?.channel_id || activeChannel?.id
      // Anchor display name too — prevents the call bar showing the wrong channel
      // when the user navigates to a different conversation mid-call.
      joinedChannelIsDMRef.current = activeChannel?.is_dm || false
      joinedChannelNameRef.current = activeChannel?.is_dm
        ? (activeChannel.other_username || 'DM')
        : activeChannel?.name || 'call'

      joinedRef.current = true
      joinedAtRef.current = Date.now()
      setElapsed(0)
      setHuddleMode('fullscreen')
      _becomeLeader()
      startRef.current()
    } else if (!isParticipant && joinedRef.current) {
      // Only auto-stop when the huddle itself is gone (huddle_ended cleared joinedHuddle).
      // If huddle still exists but isParticipant is false, it's a transient participant-list
      // update (e.g. new joiner triggers huddle_updated before their entry is added).
      // Calling stop() on that transient false → room.disconnect() → kills the call.
      // handleLeave() sets joinedRef.current = false directly, so it never enters this branch.
      if (!huddle) {
        joinedRef.current = false
        joinedAtRef.current = null
        _releaseLeadership(true)
        const savedHuddleId = lastHuddleIdRef.current
        const savedChannelId = lastChannelIdRef.current
        ;(async () => {
          await stopRef.current()
          const trackRecordings = getTrackRecordingsRef.current()
          if (savedChannelId && trackRecordings.length > 0) {
            try {
              const recordingsWithDuration = await Promise.all(
                trackRecordings.map(async (t) => ({
                  ...t,
                  durationSeconds: await getAudioDuration(t.blob),
                }))
              )
              await uploadTrackRecordings(savedChannelId, savedHuddleId, recordingsWithDuration)
            } catch (err) {
              console.error('[Huddle] Auto-stop upload failed:', err)
            }
          }
        })()
      }
    }
  }, [isParticipant, huddle])

  useEffect(() => {
    if (!isParticipant || !joinedAtRef.current) {
      setElapsed(0)
      return
    }
    function tick() {
      setElapsed(Math.floor((Date.now() - joinedAtRef.current) / 1000))
    }
    tick()
    timerRef.current = setInterval(tick, 1000)
    return () => clearInterval(timerRef.current)
  }, [isParticipant])

  // Detect new hand raises → play ting sound
  useEffect(() => {
    const allP = huddle?.participants || []
    const currentHands = new Set(allP.filter(p => p.hand_raised).map(p => p.user_id))
    // Play ting for any NEW hand raise (wasn't raised before)
    for (const uid of currentHands) {
      if (!prevHandsRef.current.has(uid)) {
        playTing()
        break // one ting per update is enough
      }
    }
    prevHandsRef.current = currentHands
  }, [huddle?.participants])

  // Detect participant joins and leaves → play audio cues (skip own join, skip initial mount)
  useEffect(() => {
    const participants = huddle?.participants || []
    const currentIds = new Set(participants.map(p => p.user_id))

    if (prevParticipantIdsRef.current === null) {
      // First time — just record state, don't play anything
      prevParticipantIdsRef.current = currentIds
      return
    }

    const prev = prevParticipantIdsRef.current
    for (const uid of currentIds) {
      if (!prev.has(uid) && uid !== currentUser?.id) {
        playJoinSound()
        break // one sound even if multiple joined at once
      }
    }
    for (const uid of prev) {
      if (!currentIds.has(uid) && uid !== currentUser?.id) {
        playLeaveSound()
        break
      }
    }
    prevParticipantIdsRef.current = currentIds
  }, [huddle?.participants, currentUser?.id])

  // PiP drag handlers — always active (lightweight when not in pip mode)
  useEffect(() => {
    function onMove(e) {
      if (!dragging.current) return
      setPipPos({
        x: Math.max(0, Math.min(window.innerWidth - 340, e.clientX - dragOffset.current.x)),
        // Keep bottom of PiP (260px tall) at least 80px from the viewport bottom
        // so it stays clear of the app's bottom nav bar.
        y: Math.max(0, Math.min(window.innerHeight - 340, e.clientY - dragOffset.current.y)),
      })
    }
    function onUp() { dragging.current = false }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  // Initialize PiP position to bottom-right every time we enter PiP mode
  useEffect(() => {
    if (huddleMode === 'pip') {
      setPipPos({ x: window.innerWidth - 350, y: window.innerHeight - 360 })
    }
  }, [huddleMode])

  // Compute these BEFORE the early return so hooks are always called in the same order
  // Sort by user_id for stable tile order — server responses may return participants
  // in different order after mute/API calls, which causes panels to visually reorder.
  const allParticipants = (huddle?.participants || []).slice().sort((a, b) => a.user_id < b.user_id ? -1 : 1)
  const participantMap = {}
  for (const p of allParticipants) {
    participantMap[p.user_id] = p.username || p.user_id.slice(0, 8)
  }
  const totalTiles = allParticipants.length +
    (isScreenSharing && screenStream ? 1 : 0) +
    Object.keys(remoteScreenStreams).length

  // ── Video grid (rendered directly — no useMemo to avoid stale stream closures) ──
  // Use STABLE keys (no huddleMode prefix) so switching modes or resizing the window
  // does NOT unmount/remount VideoTile elements — they keep their srcObject attached.
  const otherParticipants = allParticipants.filter(p => p.user_id !== currentUser?.id)
  // Collect tile props (NOT pre-rendered — so we can pass onPin/isPinned dynamically)
  const _tiles = []
  if (isScreenSharing && screenStream) {
    _tiles.push({ k: 'screen-local', p: { stream: screenStream, muted: true, mirrored: false, label: 'Your Screen', isScreen: true } })
  }
  Object.entries(remoteScreenStreams).forEach(([userId, stream]) => {
    if (stream) _tiles.push({ k: `screen-${userId}`, p: { stream, muted: true, mirrored: false, label: `${participantMap[userId] || userId.slice(0, 8)}'s Screen`, isScreen: true } })
  })
  _tiles.push({ k: 'local', p: { stream: localStream, muted: true, mirrored: true, videoOn: isVideoOn, label: currentUser?.username || 'You', isMutedIndicator: isMuted, isSpeaking: activeSpeakers.has(currentUser?.id), handRaised: allParticipants.find(pp => pp.user_id === currentUser?.id)?.hand_raised } })
  otherParticipants.forEach(p => {
    // Video element is always muted — LiveKit handles remote audio via its own <audio> elements.
    // muted={false} would block autoplay (Chrome requires user gesture for unmuted autoplay),
    // which causes video to not render after page refresh / auto-rejoin.
    _tiles.push({ k: `cam-${p.user_id}`, p: { stream: remoteStreams[p.user_id] || null, muted: true, mirrored: false, label: p.username || p.user_id.slice(0, 8), isMutedIndicator: p.is_muted, isSpeaking: activeSpeakers.has(p.user_id), handRaised: p.hand_raised } })
  })

  // Helper to render a tile with pin support
  const renderTile = (t, pinned) => (
    <VideoTile key={t.k} {...t.p} onPin={() => setPinnedKey(pinned ? null : t.k)} isPinned={pinned} />
  )

  // Find pinned tile (if any)
  const _pinned = pinnedKey ? _tiles.find(t => t.k === pinnedKey) : null
  const _rest = _pinned ? _tiles.filter(t => t.k !== pinnedKey) : []

  // Auto-pin first screen share when it appears (only once per screen share session)
  const firstScreenKey = _tiles.find(t => t.p.isScreen)?.k
  const autoPinnedRef = useRef(null) // tracks which screen key was auto-pinned
  useEffect(() => {
    if (firstScreenKey && firstScreenKey !== autoPinnedRef.current && !pinnedKey) {
      setPinnedKey(firstScreenKey)
      autoPinnedRef.current = firstScreenKey
    }
    // Reset when screen share ends so next share auto-pins again
    if (!firstScreenKey) autoPinnedRef.current = null
  }, [firstScreenKey])
  // Auto-unpin if pinned tile goes away (e.g. screen share stopped)
  useEffect(() => {
    if (pinnedKey && !_tiles.find(t => t.k === pinnedKey)) setPinnedKey(null)
  }, [pinnedKey, _tiles.length])

  // When NO tile is pinned → exact same grid as before
  // When a tile IS pinned → flex layout: big tile left, small tiles stacked right
  const videoGrid = !_pinned ? (
    <div className={`huddle-video-grid huddle-grid-${Math.min(totalTiles, 9)}`}>
      {_tiles.map(t => renderTile(t, false))}
    </div>
  ) : (
    <div style={{ display: 'flex', width: '100%', height: '100%', gap: 8, background: 'var(--bg-primary, #111)', padding: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {renderTile(_pinned, true)}
      </div>
      {_rest.length > 0 && (
        <div style={{ width: 180, display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
          {_rest.map(t => (
            <div key={t.k} style={{ width: 180, minHeight: 140, flexShrink: 0 }}>{renderTile(t, false)}</div>
          ))}
        </div>
      )}
    </div>
  )

  if ((!huddle || !isParticipant) && !leaving) return null

  const minutes = String(Math.floor(elapsed / 60))
  const seconds = String(elapsed % 60).padStart(2, '0')

  async function handleLeave() {
    setLeaving(true)
    const huddleId = huddle?.id
    const channelId = huddle?.channel_id || lastChannelIdRef.current
    setTimeout(async () => {
      // stop() awaits all per-track recorders before returning
      await stop()
      const trackRecordings = getTrackRecordings()
      joinedRef.current = false
      _releaseLeadership(true) // signal standby tabs that the leader is gone
      await leaveHuddle()
      setLeaving(false)

      // Upload every track as an individual node
      if (channelId && trackRecordings.length > 0) {
        try {
          // Decode real blob duration for each track so Synapse stores the actual
          // recorded length, not the wall-clock call span (endedAt − startedAt).
          const recordingsWithDuration = await Promise.all(
            trackRecordings.map(async (t) => ({
              ...t,
              durationSeconds: await getAudioDuration(t.blob),
            }))
          )
          await uploadTrackRecordings(channelId, huddleId, recordingsWithDuration)
        } catch (err) {
          console.error('[Huddle] Failed to upload track recordings:', err)
          // Don't lose the recording — store blob in localStorage as fallback
          // so it can be retried or manually recovered
          try {
            const fallback = await Promise.all(trackRecordings.map(async t => ({
              channelId, huddleId,
              userId: t.userId, username: t.username,
              sessionIndex: t.sessionIndex,
              startedAt: t.startedAt, endedAt: t.endedAt,
              blobBase64: await new Promise(res => {
                const reader = new FileReader()
                reader.onload = () => res(reader.result)
                reader.readAsDataURL(t.blob)
              }),
            })))
            const key = `paradigm_failed_recording_${huddleId || Date.now()}`
            localStorage.setItem(key, JSON.stringify(fallback))
            console.warn('[Huddle] Recording saved to localStorage as fallback:', key)
          } catch (fbErr) {
            console.error('[Huddle] Could not save fallback recording:', fbErr)
          }
        }
      } else if (trackRecordings.length === 0) {
        console.warn('[Huddle] No recordings to upload — mic may not have been captured (check getUserMedia permissions)')
      }
    }, 250)
  }

  async function handleToggleMute() {
    // Only flip the server-side is_muted flag (what OTHER participants see)
    // when the local toggle actually happened — toggleMute drops clicks that
    // land mid-transition, and flipping the flag anyway would leave remote
    // mute state showing the opposite of reality.
    const acted = await toggleMute()
    if (acted) await toggleHuddleMute()
  }

  const myParticipant = allParticipants.find(p => p.user_id === currentUser?.id)
  const myHandRaised = myParticipant?.hand_raised || false

  async function handleRaiseHand() {
    if (!huddle?.id) return
    try {
      await toggleRaiseHand(huddle.id)
    } catch (err) {
      console.error('[Huddle] Failed to toggle raise hand:', err)
    }
  }

  // Use the name captured at join time — activeChannel reflects wherever the user
  // is browsing right now, which may differ from the huddle's own channel.
  const channelName = joinedChannelNameRef.current || (activeChannel?.is_dm
    ? (activeChannel.other_username || 'DM')
    : activeChannel?.name)
  const channelIsDM = joinedChannelIsDMRef.current

  function onPipMouseDown(e) {
    if (e.target.closest('.huddle-pip-controls')) return
    dragging.current = true
    dragOffset.current = { x: e.clientX - pipPos.x, y: e.clientY - pipPos.y }
    e.preventDefault()
  }

  // ── Controls bar (rendered inline, not as a component, to avoid remount) ──
  function renderControls(compact) {
    return (
      <div className="huddle-bar-actions">
        {videoAvailable && (
          <Tip label={isVideoOn ? 'Turn off camera' : 'Turn on camera'}>
          <button className={`huddle-btn-glass huddle-btn-video ${!isVideoOn ? 'huddle-btn-video-off' : ''}`} onClick={toggleVideo}>
            {isVideoOn ? <VideoOnIcon /> : <VideoOffIcon />}
          </button>
          </Tip>
        )}
        <Tip label={isMuted ? 'Unmute' : 'Mute'}>
        <button className={`huddle-btn-glass huddle-btn-mute ${isMuted ? 'huddle-btn-muted' : ''}`} onClick={handleToggleMute}>
          {isMuted ? <MicOffIcon /> : <MicIcon />}
        </button>
        </Tip>
        <Tip label={myHandRaised ? 'Lower hand' : 'Raise hand'}>
        <button className={`huddle-btn-glass huddle-btn-hand ${myHandRaised ? 'huddle-btn-hand-active' : ''}`} onClick={handleRaiseHand}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 11V6a2 2 0 0 0-4 0v5" />
            <path d="M14 10V4a2 2 0 0 0-4 0v6" />
            <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
            <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
          </svg>
        </button>
        </Tip>
        {!compact && (
          <Tip label={isScreenSharing ? 'Stop sharing' : 'Share screen'}>
          <button className={`huddle-btn-glass huddle-btn-screen ${isScreenSharing ? 'huddle-btn-screen-active' : ''}`} onClick={toggleScreenShare}>
            <ScreenIcon />
          </button>
          </Tip>
        )}
        <Tip label={showGraph ? 'Hide knowledge graph' : 'Show live knowledge graph'}>
        <button
          className={`huddle-btn-glass huddle-btn-graph ${showGraph ? 'huddle-btn-graph-active' : ''}`}
          onClick={() => setShowGraph(v => !v)}
        >
          <GraphIcon />
        </button>
        </Tip>
        <Tip label="Leave call">
        <button className="huddle-btn-glass huddle-btn-leave" onClick={handleLeave}>
          <LeaveIcon />
        </button>
        </Tip>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════
  // MODE: FULLSCREEN
  // ═══════════════════════════════════════════════════
  if (huddleMode === 'fullscreen') {
    return createPortal(
      <div className={`huddle-bar-wrapper huddle-bar-fullscreen${leaving ? ' huddle-bar-leaving' : ''}`}>
        {videoGrid}
        {/* Live knowledge graph — slides in from right when toggled */}
        {showGraph && (
          <div className="live-graph-panel">
            <LiveCallGraph huddleId={huddle?.id} />
          </div>
        )}
        <div className="huddle-bar huddle-bar-overlay huddle-bar-overlay--centered">
          <div className="huddle-bar-center-info">
            <VideoOnIcon />
            <span className="huddle-bar-label">
              {channelIsDM ? channelName : `#${channelName}`}
            </span>
            <span className="huddle-bar-timer">{minutes}:{seconds}</span>
            {isRecording && (
              <Tip label={isMuted ? 'Muted — mic released, nothing is recorded' : 'Audio is being recorded'}>
              <span className={`huddle-recording-indicator${isMuted ? ' huddle-recording-paused' : ''}`}>
                <span className="huddle-recording-dot" />
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg>
                {isMuted ? 'MUTED' : 'REC'}
              </span>
              </Tip>
            )}
            <div className="huddle-bar-participants">
              {allParticipants.map(p => {
                const isCurrentUser = p.user_id === currentUser?.id
                const showMuted = isCurrentUser ? isMuted : p.is_muted
                return (
                  <Tip key={p.user_id} label={p.username + (showMuted ? ' (muted)' : '')}>
                  <div className={`huddle-participant-avatar ${showMuted ? 'huddle-participant-muted' : ''}`}>
                    {(p.username || '?').slice(0, 2).toUpperCase()}
                  </div>
                  </Tip>
                )
              })}
            </div>
          </div>
          <div className="huddle-bar-center-controls">
            <Tip label="Minimize to PiP">
            <button className="huddle-btn-minimize huddle-btn-glass" onClick={() => setHuddleMode('pip')}>
              <MinimizeIcon />
            </button>
            </Tip>
            {renderControls(false)}
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // ═══════════════════════════════════════════════════
  // MODE: PIP (draggable floating window)
  // ═══════════════════════════════════════════════════
  if (huddleMode === 'pip') {
    // Reuse the same _tiles array + VideoTile components as fullscreen mode.
    // Previously PiP built its own raw <video> elements with inline ref callbacks and a
    // one-shot hasVideo snapshot — so if a track wasn't "live" at the exact moment of
    // switching to PiP, the tile showed a placeholder forever (no update listeners).
    // VideoTile has polling + addtrack listeners + debounced track checks, so it handles
    // track state changes correctly and the video never cuts on mode switch.
    const pipCamTiles = _tiles.filter(t => !t.p.isScreen) // no screen-share tiles in PiP
    const tileWidthPct = pipCamTiles.length <= 1 ? '100%'
      : pipCamTiles.length <= 2 ? '50%'
      : `${100 / Math.min(pipCamTiles.length, 4)}%`
    return (
      <div
        className="huddle-pip"
        style={{
          position: 'fixed',
          left: pipPos.x,
          top: pipPos.y,
          width: 320,
          zIndex: 9999,
          borderRadius: 24,
          overflow: 'hidden',
          background: 'var(--bg-primary, #1a1a1a)',
          cursor: dragging.current ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={onPipMouseDown}
      >
        <div style={{ position: 'relative', height: 260, overflow: 'hidden', display: 'flex' }}>
          {pipCamTiles.map(t => (
            <div key={t.k} style={{ width: tileWidthPct, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <VideoTile {...t.p} onPin={undefined} isPinned={false} />
            </div>
          ))}
          {/* Overlaid controls at bottom center */}
          <div className="huddle-pip-controls" style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '8px 10px 12px',
            background: 'transparent',
            cursor: 'default', gap: 6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ofself-text-base)', color: '#ccc' }}>
              <span>{minutes}:{seconds}</span>
              {isRecording && (
                <Tip label={isMuted ? 'Muted — mic released, nothing is recorded' : 'Audio is being recorded'}>
                <span className={`huddle-recording-indicator${isMuted ? ' huddle-recording-paused' : ''}`}>
                  <span className="huddle-recording-dot" />
                  {isMuted ? 'MUTED' : 'REC'}
                </span>
                </Tip>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Tip label="Collapse to bar">
              <button className="huddle-btn-minimize huddle-btn-glass" onClick={() => setHuddleMode('bar')}>
                <CollapseIcon />
              </button>
              </Tip>
              <Tip label="Expand">
              <button className="huddle-btn-minimize huddle-btn-glass" onClick={() => setHuddleMode('fullscreen')}>
                <ExpandIcon />
              </button>
              </Tip>
              {renderControls(true)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════
  // MODE: BAR (thin bottom bar, audio-only feel)
  // ═══════════════════════════════════════════════════
  return (
    <div className="huddle-bottom-bar" style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
      height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 16px',
      background: 'var(--bg-secondary, #222)', borderTop: '1px solid var(--border, #333)',
      color: 'var(--text-primary, #eee)', fontSize: 'var(--ofself-text-base)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: '#4ade80', fontSize: 'var(--ofself-text-sm)' }}>&#9679;</span>
        <HeadphoneIcon />
        <span>In a call {channelIsDM ? 'with ' : 'in #'}{channelName}</span>
        <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{minutes}:{seconds}</span>
        {isRecording && (
          <Tip label={isMuted ? 'Muted — mic released, nothing is recorded' : 'Audio is being recorded'}>
          <span className={`huddle-recording-indicator${isMuted ? ' huddle-recording-paused' : ''}`}>
            <span className="huddle-recording-dot" />
            {isMuted ? 'MUTED' : 'REC'}
          </span>
          </Tip>
        )}
        <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
          {allParticipants.map(p => (
            <Tip key={p.user_id} label={p.username}>
            <div className={`huddle-participant-avatar ${(p.user_id === currentUser?.id ? isMuted : p.is_muted) ? 'huddle-participant-muted' : ''}`} style={{ width: 24, height: 24, fontSize: 'var(--ofself-text-sm)' }}>
              {(p.username || '?').slice(0, 2).toUpperCase()}
            </div>
            </Tip>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Tip label="Show PiP">
        <button className="huddle-btn-minimize huddle-btn-glass" onClick={() => setHuddleMode('pip')}>
          <ExpandIcon />
        </button>
        </Tip>
        <Tip label="Full screen">
        <button className="huddle-btn-minimize huddle-btn-glass" onClick={() => setHuddleMode('fullscreen')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
        </button>
        </Tip>
        {renderControls(true)}
      </div>
    </div>
  )
}
